import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, CalendarRange, Archive, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const eur = (n) => (Number(n) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export default function Extra() {
  const qc = useQueryClient();
  const now = new Date();
  const [anno, setAnno] = useState(now.getFullYear());
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [edits, setEdits] = useState({});
  const [showArch, setShowArch] = useState(false);

  const key = ["extra", anno, mese];
  const { data = [] } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get("/extra", { params: { anno, mese } })).data,
  });
  const { data: archived = [] } = useQuery({
    queryKey: ["extra-archivio"],
    queryFn: async () => (await api.get("/extra/archivio")).data,
    enabled: showArch,
  });

  const entrate = data.filter((r) => r.tipo === "entrata");
  const uscite = data.filter((r) => r.tipo === "uscita");
  const totEnt = entrate.reduce((s, r) => s + Number(r.importo || 0), 0);
  const totUsc = uscite.reduce((s, r) => s + Number(r.importo || 0), 0);
  const risultato = totEnt - totUsc;

  const anni = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) anni.push(y);
  const shiftMese = (delta) => {
    const d = new Date(anno, mese - 1 + delta, 1);
    setAnno(d.getFullYear());
    setMese(d.getMonth() + 1);
  };

  const addRow = async (tipo) => {
    try {
      await api.post("/extra", { anno, mese, tipo, importo: 0, descrizione: "" });
      qc.invalidateQueries({ queryKey: key });
    } catch { toast.error("Errore"); }
  };
  const patch = async (id, field, value) => {
    try {
      await api.put(`/extra/${id}`, { [field]: field === "importo" ? Number(value || 0) : value });
      qc.invalidateQueries({ queryKey: key });
    } catch { toast.error("Errore salvataggio"); }
  };
  const del = async (id) => {
    try {
      await api.delete(`/extra/${id}`);
      qc.invalidateQueries({ queryKey: key });
      toast.success("Spostata in archivio");
    } catch { toast.error("Errore"); }
  };
  const restore = async (id) => {
    try {
      await api.post(`/extra/${id}/restore`);
      qc.invalidateQueries({ queryKey: ["extra-archivio"] });
      qc.invalidateQueries({ queryKey: key });
      toast.success("Voce ripristinata");
    } catch { toast.error("Errore ripristino"); }
  };
  const val = (r, f) => (edits[r.id]?.[f] !== undefined ? edits[r.id][f] : (r[f] ?? ""));
  const setEdit = (id, f, v) => setEdits((p) => ({ ...p, [id]: { ...p[id], [f]: v } }));

  const renderColonna = (tipo, rows, colore, Icon) => (
    <div className="gr-card p-0 overflow-hidden" data-testid={`extra-col-${tipo}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: `${colore}12` }}>
        <div className="flex items-center gap-2 font-head font-bold" style={{ color: colore }}>
          <Icon size={18} /> {tipo === "entrata" ? "Entrate" : "Uscite"}
        </div>
        <div className="text-sm font-bold" style={{ color: colore }} data-testid={`extra-tot-${tipo}`}>{eur(tipo === "entrata" ? totEnt : totUsc)}</div>
      </div>
      <div className="divide-y">
        <div className="grid grid-cols-[1fr_120px_32px_36px] gap-2 px-3 py-2 text-[11px] font-bold uppercase text-muted-foreground">
          <span>Descrizione</span><span className="text-right">Importo</span><span className="text-center" title="Pagato">P</span><span></span>
        </div>
        {rows.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Nessuna voce. Aggiungi la prima.</div>}
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[1fr_120px_32px_36px] gap-2 px-3 py-1.5 items-center" data-testid={`extra-row-${r.id}`}>
            <Input
              className="h-9 text-sm" placeholder="Descrizione..."
              value={val(r, "descrizione")}
              onChange={(e) => setEdit(r.id, "descrizione", e.target.value)}
              onBlur={(e) => e.target.value !== (r.descrizione ?? "") && patch(r.id, "descrizione", e.target.value)}
              data-testid={`extra-desc-${r.id}`}
            />
            <Input
              type="text" inputMode="decimal" className="h-9 text-sm text-right" placeholder="0"
              value={val(r, "importo")}
              onChange={(e) => setEdit(r.id, "importo", e.target.value.replace(/[^\d.,]/g, "").replace(",", "."))}
              onBlur={(e) => Number(e.target.value || 0) !== Number(r.importo || 0) && patch(r.id, "importo", e.target.value)}
              data-testid={`extra-imp-${r.id}`}
            />
            <div className="flex justify-center">
              <Checkbox checked={!!r.pagato} onCheckedChange={(c) => patch(r.id, "pagato", !!c)} title="Pagato" data-testid={`extra-pagato-${r.id}`} />
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(r.id)} data-testid={`extra-del-${r.id}`}>
              <Trash2 size={15} className="text-muted-foreground" />
            </Button>
          </div>
        ))}
      </div>
      <div className="p-3 border-t">
        <Button variant="outline" size="sm" className="w-full" onClick={() => addRow(tipo)} data-testid={`extra-add-${tipo}`}>
          <Plus size={15} className="mr-1" /> Aggiungi {tipo === "entrata" ? "entrata" : "uscita"}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="extra-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMese(-1)} data-testid="extra-month-prev" title="Mese precedente"><ChevronLeft size={18} /></Button>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white shadow-sm" data-testid="extra-month-badge">
            <CalendarRange size={18} />
            <span className="font-head font-bold text-lg capitalize">{MESI[mese - 1]} {anno}</span>
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMese(1)} data-testid="extra-month-next" title="Mese successivo"><ChevronRight size={18} /></Button>
        </div>
        <Select value={String(anno)} onValueChange={(v) => setAnno(Number(v))}>
          <SelectTrigger className="h-11 w-28 font-semibold" data-testid="extra-anno-select"><SelectValue /></SelectTrigger>
          <SelectContent>{anni.map((y) => <SelectItem key={y} value={String(y)} data-testid={`extra-anno-${y}`}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" className="h-11" onClick={() => setShowArch(true)} data-testid="extra-archivio-btn"><Archive size={16} className="mr-1.5" /> Archivio</Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {renderColonna("entrata", entrate, "#1f7a5c", TrendingUp)}
        {renderColonna("uscita", uscite, "#c2410c", TrendingDown)}
      </div>

      <div className="gr-card p-5 flex flex-wrap items-center justify-between gap-4" data-testid="extra-risultato">
        <div className="flex gap-8">
          <div>
            <div className="text-xs font-bold uppercase text-muted-foreground">Totale entrate</div>
            <div className="text-xl font-head font-bold" style={{ color: "#1f7a5c" }}>{eur(totEnt)}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase text-muted-foreground">Totale uscite</div>
            <div className="text-xl font-head font-bold" style={{ color: "#c2410c" }}>{eur(totUsc)}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold uppercase text-muted-foreground">Risultato del mese</div>
          <div className="text-3xl font-head font-bold tabnum" style={{ color: risultato >= 0 ? "#1f7a5c" : "#dc2626" }} data-testid="extra-risultato-value">{eur(risultato)}</div>
        </div>
      </div>

      <Dialog open={showArch} onOpenChange={setShowArch}>
        <DialogContent className="max-w-2xl" data-testid="extra-archivio-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive size={18} /> Archivio Extra</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            {archived.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="extra-archivio-empty">Nessuna voce in archivio.</div>
            ) : (
              <div className="divide-y">
                {archived.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 py-2" data-testid={`extra-arch-row-${r.id}`}>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${r.tipo === "entrata" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>{r.tipo === "entrata" ? "Entrata" : "Uscita"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{r.descrizione || "(senza descrizione)"}</div>
                      <div className="text-xs text-muted-foreground">{MESI[(r.mese || 1) - 1]} {r.anno} · {eur(r.importo)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => restore(r.id)} data-testid={`extra-restore-${r.id}`}><RotateCcw size={14} className="mr-1.5" /> Ripristina</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
